import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Service } from '@mews-commander/data-access';
import { LocalizedText, lt, useLocalizeProperty, useLocalizeText } from '@mews/framework';
import { Banner, Button, Stack, Typography } from '@optimus-web/core';
import { FloatingPanel, TreeOption } from '@mews/b2b-ui';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { TreeField } from '@mews-commander/form';
import { RoutingRuleFormValues } from '../../../../../schemas';
import { RoutingRulesFormFields } from '../../../utils';
import { useCreateDeletedItemsCountText } from '../../../hooks';

interface RatesDialogProps {
    active: boolean;
    onClose: () => void;
    service: Service;
    rateOptions: TreeOption[];
    selectedRatesCount: string;
    totalRatesCount: string;
}

export const RatesDialog = ({
    active,
    onClose,
    service,
    rateOptions,
    selectedRatesCount,
    totalRatesCount,
}: RatesDialogProps) => {
    const { watch, setValue } = useFormContext();
    const [initialSelectedRates, setInitialSelectedRates] = useState<string[]>([]);
    const localizeProperty = useLocalizeProperty();
    const localizeText = useLocalizeText();
    const {
        formState: { errors },
        trigger,
        clearErrors,
    } = useFormContext<RoutingRuleFormValues>();

    const deletedItemsText = useCreateDeletedItemsCountText(rateOptions);
    const selectedRatesTreeTitle = `${selectedRatesCount}${UnicodeEntity.ForwardSlash}${totalRatesCount} ${localizeText(
        { textKey: TextKey.RoutingRulesFormRatesTreeRateSelected }
    )} ${deletedItemsText}`;
    const serviceName = localizeProperty(service.names);
    const title = `${localizeText({ textKey: TextKey.RoutingRulesFormRatesDialogTitle })} ${
        UnicodeEntity.LeftParenthesis
    }${serviceName}${UnicodeEntity.RightParenthesis}`;

    const handleDialogSave = async () => {
        const isValid = await trigger(
            RoutingRulesFormFields.serviceRatesType(service.id) as keyof RoutingRuleFormValues
        );
        if (!isValid) {
            return;
        }
        clearErrors();
        onClose();
    };

    const handleDialogClose = async () => {
        setValue(RoutingRulesFormFields.serviceSelectedRates(service.id), initialSelectedRates);
        const isValid = await trigger(
            RoutingRulesFormFields.serviceRatesType(service.id) as keyof RoutingRuleFormValues
        );
        if (isValid) {
            clearErrors();
        }
        onClose();
    };

    useEffect(() => {
        if (active) {
            setInitialSelectedRates(watch(RoutingRulesFormFields.serviceSelectedRates(service.id)));
        }
    }, [watch, active, service.id]);

    const footer = {
        primary: (
            <Button
                variant="primary"
                type="submit"
                size="medium"
                onClick={handleDialogSave}
                data-test-id={DataTestId.RoutingRulesRatesDialogSave}
            >
                {lt(TextKey.Save)}
            </Button>
        ),
        secondary: (
            <Button
                variant="tertiary"
                type="button"
                size="medium"
                onClick={handleDialogClose}
                data-test-id={DataTestId.RoutingRulesRatesDialogCancel}
            >
                {lt(TextKey.Cancel)}
            </Button>
        ),
    };

    return (
        <FloatingPanel active={active} onClose={handleDialogClose} title={title} footer={footer}>
            <Stack vertical spacing="200">
                <Typography variant="secondary" textStyle="bodyMedium" as="div">
                    <LocalizedText
                        textKey={TextKey.RoutingRulesFormRatesDialogDescription}
                        substitutes={{ service: serviceName }}
                    />
                </Typography>
                {errors?.itemsAndConditions?.services?.[service.id]?.serviceRates?.type && (
                    <Banner
                        variant="error"
                        primaryText={
                            <LocalizedText
                                textKey={
                                    // @ts-expect-error Property textKey does not exist on type
                                    errors?.itemsAndConditions?.services?.[service.id]?.serviceRates?.type?.textKey
                                }
                            />
                        }
                    />
                )}
                <TreeField
                    name={RoutingRulesFormFields.serviceSelectedRates(service.id)}
                    multiple
                    options={rateOptions}
                    defaultExpandAll
                    showSelectAllToggle
                    showSearchBar
                    title={
                        <Typography variant="primary" textStyle="bodyMedium" as="div">
                            {selectedRatesTreeTitle}
                        </Typography>
                    }
                />
            </Stack>
        </FloatingPanel>
    );
};
